import React from 'react';
import { useTheme } from 'styled-components';

import { Loading } from '../loading/loading';
import { Tooltip } from '../tooltip/tooltip';
import { ShadowSizeType } from '../../../theme/type/shadow-type';
import { CardVariantType } from '../../../theme/type/card-type';
import { RadiusSizeType } from '../../../theme/type/radius-type';
import { GapSizeType } from '../../../theme/type/gap-type';
import { BreakpointSizeType } from '../../../theme/type/breakpoints-type';
import { createStableClassToken } from '../../../utils/stable-class-token';

interface CardPropsInterface {
    children: Array<JSX.Element> | JSX.Element | string;
    title?: string | React.ReactNode;
    footer?: Array<JSX.Element> | JSX.Element | string;
    action?: React.ReactNode;
    tooltipText?: React.ReactNode;
    variant?: CardVariantType;
    shadow?: ShadowSizeType;
    radius?: RadiusSizeType;
    padding?: GapSizeType | object;
    loading?: boolean;
    marginBottom?: GapSizeType;
    marginTop?: GapSizeType;
    fullWidth?: boolean;
}

const isResponsivePadding = (
    value: GapSizeType | object,
): value is Partial<Record<BreakpointSizeType, GapSizeType>> => {
    return typeof value === 'object';
};

export const Card: React.FC<CardPropsInterface> = ({
    children,
    title,
    footer,
    variant = 'default',
    radius = 'medium',
    shadow,
    padding = 'medium',
    action,
    tooltipText,
    loading = false,
    marginBottom,
    marginTop,
    fullWidth,
}) => {
    const theme = useTheme();
    const responsivePaddingClassName = createStableClassToken('card-padding', [padding]);

    let responsivePaddingCss = '';
    if (isResponsivePadding(padding)) {
        const entries = Object.entries(padding) as [BreakpointSizeType, GapSizeType][];
        responsivePaddingCss = entries
            .map(([breakpoint, gap], index) => {
                const value = theme.gap[gap];
                const maxQuery = theme.breakpoints[breakpoint].max;
                const minQuery = theme.breakpoints[breakpoint].min;

                if (index === 0) {
                    return `
                        @media ${maxQuery} {
                            .${responsivePaddingClassName} {
                                padding: ${value};
                            }
                        }
                        @media ${minQuery} {
                            .${responsivePaddingClassName} {
                                padding: ${value};
                            }
                        }
                    `;
                }

                return `
                    @media ${minQuery} {
                        .${responsivePaddingClassName} {
                            padding: ${value};
                        }
                    }
                `;
            })
            .join('\n');
    }

    const actionHoverClassName = createStableClassToken('card-action', [variant]);

    const CardComponent = () => (
        <div
            className={`relative flex min-h-full flex-col ${
                isResponsivePadding(padding) ? responsivePaddingClassName : ''
            } ${actionHoverClassName}`}
            style={{
                borderRadius: theme.radius[radius],
                border: `0.1rem solid ${theme.card.variant[variant].border}`,
                width: fullWidth ? '100%' : undefined,
                padding: typeof padding === 'string' ? theme.gap[padding] : undefined,
                boxShadow: shadow ? theme.shadow[shadow] : undefined,
                background: theme.card.variant[variant].background,
                color: theme.card.variant[variant].color,
                marginTop: marginTop ? theme.gap[marginTop] : undefined,
                marginBottom: marginBottom ? theme.gap[marginBottom] : undefined,
            }}
        >
            {responsivePaddingCss ? <style>{responsivePaddingCss}</style> : null}
            <style>{`
                .${actionHoverClassName} [data-dropdown-wrapper]:hover [data-dropdown-trigger] i {
                    color: ${theme.card.variant[variant].icon.hover};
                }
            `}</style>
            {title && (
                <div className="z-[900] mb-small flex flex-wrap items-center gap-xsmall">
                    <div
                        className="flex-1 font-bold uppercase"
                        style={{
                            color: theme.card.variant[variant].title.color,
                            fontSize: theme.text.size.vsmall.fontSize,
                            lineHeight: theme.text.size.vsmall.lineHeight,
                        }}
                    >
                        {title}
                    </div>
                    {tooltipText && (
                        <Tooltip position="top-right" icon content={tooltipText}>
                            <></>
                        </Tooltip>
                    )}
                    <div style={{ color: theme.card.variant[variant].icon.color }}>{action}</div>
                </div>
            )}
            <div className="flex flex-1 flex-col" style={{ color: theme.card.variant[variant].color }}>
                {children}
            </div>
            {footer && <div className="z-[999] mt-small flex justify-end">{footer}</div>}
        </div>
    );

    if (loading) {
        return (
            <Loading active={loading}>
                <CardComponent />
            </Loading>
        );
    }

    return <CardComponent />;
};
